import { monitoringTools } from "@shared/utils/constants";
import { getEntitiesByKeyList, removeDuplicatesByFields } from "@shared/components/form-builder/utils";
import { unknownStatusList } from "./constants";

const extractFieldName = (input) => {
  // extract field name from the mask "<field_name>":
  if (typeof input === 'string') {
    const match = input.match(/^<(.+)>$/);
    return match ? match[1] : null;
  } else {
    return null;
  }
}

const prebuiltQueryParser = {
  parsers: {
    default: (prebuiltQuery, entityForm) => {
      if (!prebuiltQuery || typeof prebuiltQuery !== "object") return null;
      // prepare prebuilt query payload based on prebuiltQuery:
      return Object.entries(prebuiltQuery)
        .reduce((result, [key, value]) => {
          // recursively check nested properties:
          if (typeof value === "object" && value !== null && !Array.isArray(value)) {
            const sections = entityForm?.formData?.sections ?? [];
            
            if ("address_line" in value  || "city" in value || "country" in value || "postal_code" in value || "state" in value) {          
              const parsedObj = {};
                Object.entries(value).forEach(([fieldKey, fieldValue]) => {
                  if (typeof fieldValue === "string" && fieldValue.includes("<")) {
                    // Extract all placeholders of the format <Section.Field>
                    const matches = [...fieldValue.matchAll(/<(.+?)\.(.+?)>/g)];

                    if (matches.length > 0) {
                      let resolvedValue = fieldValue;

                      for (const match of matches) {
                        const sectionLabel = match[1]?.trim();
                        const fieldName = match[2]?.trim();

                        if (sectionLabel && fieldName) {
                          // Step 1: Find the section
                          const targetSection = sections.find(section =>
                            section.entities.some(entity =>
                              entity.block_label?.toLowerCase() === sectionLabel.toLowerCase()
                            )
                          );

                          // Step 2: Find the entity block
                          const targetEntityBlock = targetSection?.entities.find(
                            entity => entity.block_label?.toLowerCase() === sectionLabel.toLowerCase()
                          );

                          // Step 3: Find the field inside that block
                          const targetField = targetEntityBlock?.entities.find(
                            field => field.field_internal_name.toLowerCase() === fieldName.toLowerCase()
                          );
                          console.log("targetField  ", targetField)

                          const fieldVal = targetField?.value ?? "";

                          // Replace this specific placeholder with its value
                          resolvedValue = resolvedValue.replace(match[0], fieldVal);
                        }
                      }

                      parsedObj[fieldKey] = resolvedValue.trim(); // Final composed string
                    } else {
                      parsedObj[fieldKey] = "";
                    }
                  } else {
                    // Not a templated string — use as-is
                    parsedObj[fieldKey] = fieldValue;
                  }
                });
          
              // ✅ Assign after fully building
              result[key] = parsedObj;
          
            } else {
              // Normal nested object parsing
              const parsedValue = prebuiltQueryParser.parsers.default(value, entityForm);
              if (parsedValue !== null) {
                result[key] = parsedValue;
              }
            }
          }
          else if (Array.isArray(value)) {
              const sections = entityForm?.formData?.sections ?? [];
            
              result[key] =  value.map(template => {
                const parsedObj = {};
            
                if (typeof template == "object" && value !== null) {         
                  Object.entries(template).forEach(([fieldKey, fieldValue]) => {

                    if (typeof fieldValue === "string" && fieldValue.startsWith("<")) {
                      const match = fieldValue.match(/<(.+?)\.(.+?)>/);
                      const sectionLabel = match?.[1];
                      const fieldName = match?.[2];
              
                      if (sectionLabel && fieldName) {
                        const targetSection = sections.find(section =>
                          section.entities.some(entity =>
                            entity.block_label?.toLowerCase() === sectionLabel.toLowerCase()
                          )
                        );
                        
                        // Step 2: Find the entity block
                        const targetEntityBlock = targetSection?.entities.find(
                          entity => entity.block_label?.toLowerCase() === sectionLabel.toLowerCase()
                        );
                        
                        // Step 3: Find the field inside that block
                        const targetField = targetEntityBlock?.entities.find(
                          field => field.field_internal_name.toLowerCase() === fieldName.toLowerCase()
                        );
              
                        parsedObj[fieldKey] = targetField?.value ?? "";
                      } else {
                        parsedObj[fieldKey] = "";
                      }
                    } else {
                      // Static value
                      parsedObj[fieldKey] = fieldValue;
                    }
                  });
                } else {
                  // Normal nested object parsing
                  const parsedValue = prebuiltQueryParser.parsers.default(template, entityForm);
                  if (parsedValue !== null) {
                    result[key] = parsedValue;
                  }
                }
            
                return parsedObj;
              });
             
            }
          else {
            const fieldName = extractFieldName(value);
            // if there is a plain value add it to the payload:
            if (!fieldName) result[key] = value;
            else {
              // find the field in the entity's form:
              const fields = getEntitiesByKeyList(entityForm?.formData?.sections, { field_internal_name: fieldName });
              // add a value only if there is only one field with such a type:
              if (fields.length === 1) {
                const field = fields[0];
                if (field?.value) {
                  result[key] = field.value;
                }
              }
              else if(fields.length > 1){
                const uniqueFields = removeDuplicatesByFields(fields);
                const field = uniqueFields[0] 
                if (field?.value) {
                  result[key] = field.value;
                }
              }
            }
          }

          return result;

        }, {})
    },
    [monitoringTools.SEARCH_ENGINE]: (prebuiltQuery, entityForm) => {
      const query = prebuiltQuery?.search_query;
      if (typeof query !== "string") return null;

      return query.replace(/<([^>]+)>/g, (_, fieldName) => {
        let fieldValue = "";
        const fields = getEntitiesByKeyList(entityForm?.formData?.sections, { field_internal_name: fieldName });
        if (fields.length === 1) {
          const field = fields[0];
          if (field?.value) {
            fieldValue = field.value;
          }
        }
        else if(fields.length > 1){
          const uniqueFields = removeDuplicatesByFields(fields);
          const field = uniqueFields[0] 
          if (field?.value) {
            fieldValue = field.value;
          }
        }
        return fieldValue;
      });
    },
  },
  parse(query, entityForm, toolName) {
    const currentParser = this.parsers[toolName] || this.parsers.default;
    try {
      return currentParser(query, entityForm);
    } catch (err) {
      console.error("Parser error:", err);
      return null;
    }
  }
}

export const parsePrebuiltQuery = (query, entityForm, toolName) => {
  return prebuiltQueryParser.parse(query, entityForm, toolName);
}

export const checkUnknownStatus = (status) => unknownStatusList.includes(status);